import { clearBearer, getBearer } from "./auth.js";
import type { HomeLayout } from "./homeLayout.js";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * Public endpoints that can legitimately return 401 without meaning
 * the user's session has expired (e.g. pair/claim flows).
 */
const AUTH_PATHS = [
  "/api/auth/",
  "/api/playback/devices",
];

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getBearer();
  const headers: Record<string, string> = { accept: "application/json" };
  if (token) headers["authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["content-type"] = "application/json";

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (typeof j?.error === "string") detail = j.error;
    } catch {
      /* ignore */
    }

    // A 401 from a protected endpoint means the stored token is invalid or
    // expired. Nuke it and bounce to the login page.
    if (res.status === 401 && !AUTH_PATHS.some((p) => path.startsWith(p))) {
      clearBearer();
      window.location.replace("/login");
    }

    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export const api = {
  get:  <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch:<T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put:  <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del:  <T>(path: string) => request<T>("DELETE", path),
};

// ---- typed wrappers ----

/** `?device=<id>` — multi-Pi tunnel routing (2026-10-28). The cloud resolves
 *  the receiver's own tunnel port from cast_receivers; omitted = the legacy
 *  single-Pi fallback. */
const devQ = (deviceId?: string): string =>
  deviceId ? `?device=${encodeURIComponent(deviceId)}` : "";

export interface Profile {
  id: string;
  display_name: string;
  avatar_emoji: string | null;
  forecourt_mark: string | null;
  onboarding_complete: boolean;
  created_at: string;
}

/** A login account (not a viewer profile) as the admin users list returns it. */
export interface AuthUser {
  id: string;
  username: string;
  display_name: string | null;
  is_admin: boolean;
  has_pin: boolean;
  created_at: string;
  last_login_at: string | null;
}

/** Minimum metadata shared by every dismissable Home card. Optional genre/tag
 * fields are folded into one negative taste snapshot by the API wrapper. */
export interface DismissalItem {
  id: string;
  title: string;
  kind: string;
  tags?: string[] | null;
  tmdb_genres?: string[] | null;
  genres?: string[] | null;
  creator_id?: string | null;
}

export interface LibraryItem {
  id: string;
  kind: string;
  title: string;
  thumbnail_url: string | null;
  duration_s: number | null;
  category: string | null;
  creator_name: string | null;
  published_at: string | null;
  source_id: number;
  tags: string[] | null;
  tmdb_rating?: number | null;
  tmdb_genres?: string[] | null;
}

export interface LibraryPage {
  total: number;
  limit: number;
  offset: number;
  items: LibraryItem[];
}

/** One credited person — principal cast or episode guest star. */
export interface CastMember {
  name: string;
  character?: string | null;
  profile_url?: string | null;
}

export interface MediaItemDetail {  profile_id: string;
  item: LibraryItem & {
    description: string | null;
    stream_url: string | null;
    external_url: string | null;
    tvg_id: string | null;
    language: string | null;
    creator_id: string | null;
    /** ISO 639-1 original language from TMDb (null if unenriched). Drives
     *  automatic subtitle selection on the TV. */
    original_language?: string | null;
    /** Principal cast (rd: items — live TMDB, with photos). */
    cast?: CastMember[];
    /** Movie director(s), or series creator(s) for tv items (rd: branch). */
    director?: string | null;
    /** Episode-only guest stars (rd: series episodes). */
    guest_stars?: CastMember[];
    /** Library (DB) items: top-billed cast names from the credits backfill. */
    tmdb_cast_top?: string[] | null;
    tmdb_director?: string | null;
  };
  playback:
    | { mode: "youtube_intent"; external_url: string }
    | {
        mode: "exoplayer";
        stream_url: string;
        via_hls?: boolean;
        audio_tracks?: AudioTrack[];
        audio_track?: number | null;
        transcode_reason?: string | null;
        video_codec?: string | null;
        video_playable_in_browser?: boolean;
        subtitle_tracks?: SubtitleTrack[];
        forced_subtitle?: number | null;
      };
  resume: { position_s: number; last_event: string; last_seen_at: string } | null;
}

/**
 * One audio track inside a release. Multi-audio files are the norm for anime
 * and European scene releases; the server picks English by default and hands
 * the whole list over so the player can offer a switch.
 */
export interface AudioTrack {
  /** Index among audio streams only — what the server maps with -map 0:a:<n>. */
  index: number;
  codec: string | null;
  language: string | null;
  title: string | null;
  channels: number | null;
  is_default: boolean;
  is_commentary: boolean;
  /** Ready-made display label: "English · 5.1 · DTS". */
  label: string;
  is_english: boolean;
}

/** One subtitle stream inside a release. */
export interface SubtitleTrack {
  index: number;
  codec: string | null;
  language: string | null;
  title: string | null;
  /** Carries only translated signs and foreign dialogue — shown unasked. */
  forced: boolean;
  is_default: boolean;
  /** False for bitmap subs (PGS/VOBSUB), which we can't convert to WebVTT. */
  is_text: boolean;
  label: string;
}

/** One candidate release behind an rd: item, as listed by /tv/item/:id/sources. */
export interface DebridSource {
  src_id: string;
  display_title: string;
  quality: string;
  hdr: boolean;
  codec: string | null;
  size_label: string | null;
  size_bytes: number | null;
  seeders: number | null;
  source: string | null;
  resolved_by: "real-debrid" | "premiumize" | "alldebrid" | "comet";
  /**
   * Whether the debrid service already holds the file. `false` means it would
   * have to download the torrent first, and serves a placeholder until then.
   */
  cached?: boolean | null;
  /**
   * Whether a browser can decode this release's video. Video is copied
   * untouched through the transcoder, so playback can't rescue a `false`.
   */
  browser_friendly?: boolean;
  /** Other debrid providers carrying this same release, used as fallbacks. */
  alt_providers: string[];
}

/** A specific candidate resolved to a playable URL. */
export interface ResolvedSource {
  src_id: string;
  /** Raw CDN url — what mpv on the Pi should be given. */
  stream_url: string;
  /** Browser-playable url — an HLS session playlist when a remux is needed. */
  play_url: string;
  needs_transcode: boolean;
  /** True when play_url is an HLS playlist rather than the raw stream. */
  via_hls?: boolean;
  audio_codec: string | null;
  /** Every audio track in the release, for the language switcher. */
  audio_tracks?: AudioTrack[];
  /** Index into audio_tracks the server chose (English-first). */
  audio_track?: number | null;
  /** Why a remux was needed ("container matroska; audio dts; 2 audio tracks"). */
  transcode_reason?: string | null;
  duration_s?: number | null;
  video_codec?: string | null;
  /** False when the browser probably can't decode the video (HEVC at 2160p). */
  video_playable_in_browser?: boolean;
  subtitle_tracks?: SubtitleTrack[];
  forced_subtitle?: number | null;
  validated: boolean;
  resolved_by: string;
  /** How many provider routes were tried before one answered. */
  providers_tried: number;
  source: DebridSource;
}

export interface NetflixItem {
  id: string;           // rd:movie:tt… | rd:series:tt…
  title: string;
  kind: "movie" | "series";
  imdb_id: string;
  year: number | null;
  poster_url: string | null;
  synopsis: string | null;
  genres?: string[];
}

/** Map a Netflix catalog item to the LibraryItem shape MediaStrip expects. */
export function netflixToLibraryItem(n: NetflixItem): LibraryItem {
  return {
    id: n.id,
    kind: n.kind === "series" ? "tv_show" : "vod",
    title: n.title,
    thumbnail_url: n.poster_url,
    category: null,
    tags: n.genres ?? [],
    duration_s: null,
    episode_count: undefined,
  } as unknown as LibraryItem;
}

/** CuriosityStream catalog item — plays directly (cs: id → DRM-free HLS). */
export interface CuriosityItem {
  id: string;
  media_id: number;
  title: string;
  description: string | null;
  year: number | null;
  duration_s: number | null;
  category: string | null;
  quality: string | null;
  rating_percentage: number | null;
  poster_url: string | null;
  backdrop_url: string | null;
}

/** Map a CuriosityStream item to the LibraryItem shape MediaStrip expects. */
export function curiosityToLibraryItem(c: CuriosityItem): LibraryItem {
  return {
    id: c.id,
    kind: "vod",
    title: c.title,
    thumbnail_url: c.poster_url,
    category: c.category,
    tags: c.category ? [c.category] : [],
    duration_s: c.duration_s,
    episode_count: undefined,
  } as unknown as LibraryItem;
}

/** One magic-spinner pick — same id contract as NetflixItem plus the AI's why. */
export interface SpinItem {
  id: string;
  title: string;
  kind: "movie" | "series";
  year: number | null;
  poster_url: string | null;
  origin: string;
  genres?: string[];
  why: string | null;
  /** Local series picks link the series page via this, not an episode's watch page. */
  series_category?: string;
}

export interface SpinHeat {
  count: number;
  label: "cold" | "warming" | "warm" | "hot";
}

/** IMDb-style person page payload. */
export interface PersonCredits {
  person: {
    name: string;
    photo_url: string | null;
    known_for: string | null;
    biography: string | null;
    birthday: string | null;
    place_of_birth: string | null;
  };
  movies: NetflixItem[];
  series: NetflixItem[];
}

export interface Source {
  id: number;
  kind: "youtube" | "m3u" | "xmltv" | "xtream";
  label: string;
  url: string | null;
  refresh_minutes: number;
  last_synced_at: string | null;
  last_error: string | null;
  enabled: boolean;
}

export interface OnboardingTitle {
  id: string;
  title: string;
  year: number | null;
  kind: "movie" | "tv";
  tags: string[];
  poster_url: string | null;
  rated: boolean;
}

export interface OnboardingTopic {
  key: string;
  label: string;
  rated: boolean;
}

export interface OnboardingPayload {
  complete: boolean;
  titles: OnboardingTitle[];
  topics: OnboardingTopic[];
}

export interface OnboardingCompleteResult {
  ok: boolean;
  preview: {
    tag_weights: Record<string, number>;
    n_titles_rated: number;
    n_topics_rated: number;
  };
}

export interface ProfilePreferences {
  preferences: {
    profile_id: string;
    tag_weights: Record<string, number>;
    creator_weights: Record<string, number>;
    style_aversions: Record<string, number>;
    duration_pref_by_tag: Record<string, unknown>;
    updated_at: string;
  };
}

export const Api = {
  health: () => api.get<{ ok: boolean; pg: { ok: boolean; error: string | null } }>("/health"),
  auth: {
    register: (body: { username: string; password: string; display_name?: string }) =>
      api.post<{ user: { id: string; username: string; display_name: string | null; is_admin: boolean } }>("/api/auth/register", body),
    login: (body: { username: string; password: string; device_label?: string; remember?: boolean }) =>
      api.post<{
        token: string;
        expires_at: string;
        user: { id: string; username: string; display_name: string | null; is_admin: boolean };
      }>("/api/auth/login", body),
    me: () =>
      api.get<{
        user: { id: string; username: string; display_name: string | null; is_admin: boolean };
        session: { id: string; device_label: string | null; expires_at: string };
      }>("/api/auth/me"),
    logout: () => api.post<void>("/api/auth/logout"),
    sessions: () =>
      api.get<{
        current_session_id: string;
        sessions: { id: string; device_label: string | null; user_agent: string | null; ip: string | null; created_at: string; last_used_at: string | null; expires_at: string }[];
      }>("/api/auth/sessions"),
    revokeSession: (id: string) => api.post<{ ok: boolean }>(`/api/auth/sessions/${id}/revoke`),
    pairingStart: () =>
      api.post<{ code: string; expires_at: string }>("/api/auth/pairing/start"),
    pairingStatus: (code: string) =>
      api.get<{ status: "pending" | "approved" | "expired"; token?: string; expires_at?: string; device_label?: string | null }>(`/api/auth/pairing/${encodeURIComponent(code)}/status`),
    pairingApprove: (code: string, body: { device_label?: string }) =>
      api.post<{ ok: boolean; error?: string }>(`/api/auth/pairing/${encodeURIComponent(code)}/approve`, body),
    pairingGoogleClaim: (code: string, credential: string, device_label?: string) =>
      api.post<{
        ok: boolean;
        user?: { id: string; username: string; display_name: string | null; created: boolean };
        error?: string;
      }>(`/api/auth/pairing/${encodeURIComponent(code)}/google-claim`, { credential, device_label }),
    /** Admin: login-user management (PIN pad is the only sign-in, so these
     *  are how accounts get their PINs). */
    users: {
      list: () => api.get<{ users: AuthUser[] }>("/api/auth/users"),
      create: (body: { username: string; display_name?: string; pin: string }) =>
        api.post<{ user: { id: string; username: string; display_name: string | null; is_admin: boolean } }>("/api/auth/users", body),
      setPin: (id: string, pin: string | null) =>
        api.post<{ ok: boolean }>(`/api/auth/users/${encodeURIComponent(id)}/pin`, { pin }),
    },
  },
  googleConfig: () => api.get<{ client_id: string | null }>("/api/auth/google/config"),
  pairing: {
    start: () =>
      api.post<{ code: string; expires_at: string }>("/api/auth/pairing/start"),
    status: (code: string) =>
      api.get<{ status: "pending" | "approved" | "expired"; token?: string; expires_at?: string; device_label?: string | null }>(`/api/auth/pairing/${encodeURIComponent(code)}/status`),
    googleClaim: (code: string, credential: string, device_label?: string) =>
      api.post<{
        ok: boolean;
        user?: { id: string; username: string; display_name: string | null; created: boolean };
        error?: string;
      }>(`/api/auth/pairing/${encodeURIComponent(code)}/google-claim`, { credential, device_label }),
  },
  profiles: {
    list: () => api.get<{ profiles: Profile[] }>("/api/profiles"),
    create: (input: { display_name: string; avatar_emoji?: string | null; forecourt_mark?: string | null }) =>
      api.post<{ profile: Profile }>("/api/profiles", input),
    remove: (id: string) => api.del<void>(`/api/profiles/${id}`),
    getHomeLayout: (id: string) =>
      api.get<{ layout: HomeLayout }>(
        `/api/profiles/${encodeURIComponent(id)}/home-layout`,
      ),
    putHomeLayout: (id: string, layout: HomeLayout) =>
      api.put<{ layout: HomeLayout }>(
        `/api/profiles/${encodeURIComponent(id)}/home-layout`,
        layout,
      ),
    getCarouselState: (id: string) =>
      api.get<{ state: Record<string, { itemIds: string[]; lastRefreshAt: number }> }>(
        `/api/profiles/${encodeURIComponent(id)}/carousel-state`,
      ),
    putCarouselState: (id: string, state: Record<string, { itemIds: string[]; lastRefreshAt: number }>) =>
      api.put<{ ok: boolean }>(
        `/api/profiles/${encodeURIComponent(id)}/carousel-state`,
        state,
      ),
  },
  subtitles: {
    search: (params: { imdb_id: string; kind: "movie" | "series"; season?: number; episode?: number; languages?: string }) => {
      const qs = new URLSearchParams();
      qs.set("imdb_id", params.imdb_id);
      qs.set("kind", params.kind);
      if (params.season !== undefined) qs.set("season", String(params.season));
      if (params.episode !== undefined) qs.set("episode", String(params.episode));
      if (params.languages) qs.set("languages", params.languages);
      return api.get<{
        imdb_id: string; kind: string; season?: number; episode?: number; languages: string;
        tracks: Array<{
          file_id: number;
          language: string;
          release: string | null;
          hearing_impaired: boolean;
          ai_translated: boolean;
          machine_translated: boolean;
          download_count: number;
          from_trusted: boolean;
          forced: boolean;
          display_label: string;
        }>;
      }>(`/tv/subtitles/search?${qs.toString()}`);
    },
    trackUrl: (fileId: number) => `/tv/subtitles/track/${fileId}`,
  },
  likeThis: (itemId: string, profileId: string) =>
    api.get<{
      source: { id: string; kind: string; title: string; thumbnail_url: string | null; genres: string[]; year: number | null };
      axes: { kind: "genre" | "decade" | "genre_decade" | "keyword" | "creator" | "director" | "cast" | "studio"; label: string; items: Array<{
        id: string; kind: string; title: string; category: string | null; thumbnail_url: string | null; backdrop_url: string | null;
        tmdb_rating: number | null; release_year: number | null; episode_count?: number;
      }> }[];
    }>(`/tv/item/${encodeURIComponent(itemId)}/like-this?profile_id=${encodeURIComponent(profileId)}`),
  mood: {
    turn: (input: {
      profile_id: string;
      conversation: { role: "user" | "assistant"; content: string }[];
    }) =>
      api.post<
        | { phase: "ask"; question: string; options?: string[] | null }
        | { phase: "reply"; message: string }
        | { phase: "results"; blurb: string | null; filters: Record<string, unknown>; items: Array<{
            id: string;
            kind: string;
            title: string;
            thumbnail_url: string | null;
            backdrop_url: string | null;
            tmdb_rating: number | null;
            tmdb_genres: string[] | null;
            tmdb_overview: string | null;
            duration_s: number | null;
            category: string | null;
            release_year: number | null;
          }> }
      >("/tv/mood", input),
  },
  libraryWithDebrid: (params: {
    q: string;
    kind?: string;
    year_from?: number;
    year_to?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("q", params.q);
    if (params.kind) qs.set("kind", params.kind);
    if (params.year_from) qs.set("year_from", String(params.year_from));
    if (params.year_to) qs.set("year_to", String(params.year_to));
    if (params.limit) qs.set("limit", String(params.limit));
    return api.get<{
      local: { total: number; items: LibraryItem[] };
      debrid: { items: Array<LibraryItem & { via: "real-debrid"; overview: string | null; tmdb_backdrop_url: string | null; tmdb_rating: number | null }> };
    }>(`/tv/library/with-rd?${qs.toString()}`);
  },
  dismissals: {
    list: (profileId: string) =>
      api.get<{ itemIds: string[] }>(`/api/profiles/${encodeURIComponent(profileId)}/dismissals`),
    dismiss: (profileId: string, item: DismissalItem) => {
      const tags = [...new Set([
        ...(item.tags ?? []),
        ...(item.tmdb_genres ?? []),
        ...(item.genres ?? []),
      ].map((t) => t.trim()).filter(Boolean))].slice(0, 50);
      return api.post<{ ok: boolean }>(`/api/profiles/${encodeURIComponent(profileId)}/dismissals`, {
        item_id: item.id,
        title: item.title,
        kind: item.kind,
        tags,
        creator_id: item.creator_id ?? null,
      });
    },
  },
  recsTo: {
    send: (input: {
      from_profile_id: string;
      to_profile_ids: string[];
      item_id: string;
      note?: string;
    }) =>
      api.post<{ recommendations: { id: string; to_profile_id: string }[] }>(
        "/api/recommendations",
        input,
      ),
    dismiss: (id: string) =>
      api.post<{ ok: boolean }>(
        `/api/recommendations/${encodeURIComponent(id)}/dismiss`,
      ),
  },
  sources: {
    list: () => api.get<{ sources: Source[] }>("/api/sources"),
    create: (input: { kind: Source["kind"]; label: string; url?: string; username?: string; password?: string }) =>
      api.post<{ source: Source }>("/api/sources", input),
    remove: (id: number) => api.del<void>(`/api/sources/${id}`),
    refresh: (id: number) => api.post<{ queued: boolean }>(`/admin/refresh/${id}`),
  },
  onboarding: {
    payload: (profileId: string) => api.get<OnboardingPayload>(`/api/profiles/${profileId}/onboarding`),
    rateTitle: (profileId: string, body: { title_id: string; seen: boolean; rating: number | null }) =>
      api.post<void>(`/api/profiles/${profileId}/onboarding/rating`, { kind: "title", ...body }),
    rateTopic: (profileId: string, body: { topic: string; rating: number }) =>
      api.post<void>(`/api/profiles/${profileId}/onboarding/rating`, { kind: "topic", ...body }),
    complete: (profileId: string) => api.post<OnboardingCompleteResult>(`/api/profiles/${profileId}/onboarding/complete`),
    preferences: (profileId: string) => api.get<ProfilePreferences>(`/api/profiles/${profileId}/preferences`),
  },
  live: {
    categories: () => api.get<{ categories: { name: string; count: number }[] }>("/tv/live/categories"),
    list: (profileId: string, category?: string) => {
      const qs = new URLSearchParams({ profile_id: profileId });
      if (category) qs.set("category", category);
      return api.get<{
        profile_id: string;
        items: {
          id: string;
          kind: string;
          title: string;
          thumbnail_url: string | null;
          tvg_id: string | null;
          category: string | null;
          now_title: string | null;
          now_start: string | null;
          now_stop: string | null;
          now_category: string | null;
          next_title: string | null;
          next_start: string | null;
        }[];
      }>(`/tv/live?${qs.toString()}`);
    },
  },
  recommendations: (profileId: string) =>
    api.get<{
      profile_id: string;
      sections: {
        kind: string;
        label: string;
        items: (LibraryItem & {
          _ranking?: { pool: "exploit" | "explore"; slot: number; score: number };
          last_position_s?: number;
          last_seen_at?: string;
          last_event?: string;
          duration_s?: number | null;
        })[];
      }[];
    }>(`/tv/recommendations?profile_id=${encodeURIComponent(profileId)}`),
  history: (profileId: string) =>
    api.get<{
      profile_id: string;
      items: (LibraryItem & { last_position_s: number; last_seen_at: string; last_event: string })[];
    }>(`/tv/history?profile_id=${encodeURIComponent(profileId)}`),
  library: {
    list: (params: { kind?: string; q?: string; source_id?: number; year_from?: number; year_to?: number; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
      }
      return api.get<LibraryPage & { mode?: "lexical" | "fuzzy" | "browse" }>(`/tv/library?${qs.toString()}`);
    },
    searchYouTube: (q: string) =>
      api.get<{ query: string; items: { video_id: string; title: string; channel: string | null; description: string | null; published_at: string | null; thumbnail_url: string | null }[] }>(
        `/tv/library/youtube?q=${encodeURIComponent(q)}`,
      ),
    // Netflix catalog (discovery only — items carry rd: ids and play via Real-Debrid).
    searchNetflix: (q: string) =>
      api.get<{ query: string; items: NetflixItem[] }>(`/tv/netflix/search?q=${encodeURIComponent(q)}`),
    netflixTrending: () =>
      api.get<{ items: NetflixItem[] }>(`/tv/netflix/trending`),
    // CuriosityStream — Douglas's subscription; cs: ids play their HLS directly.
    searchCuriosity: (q: string) =>
      api.get<{ query: string; items: CuriosityItem[] }>(`/tv/curiosity/search?q=${encodeURIComponent(q)}`),
    curiosityFeatured: () =>
      api.get<{ items: CuriosityItem[] }>(`/tv/curiosity/featured`),
    /** "Hot on <service>" strips — apple | hbo | cable | nfb (TMDB discover). */
    providerTrending: (key: "apple" | "hbo" | "cable" | "nfb") =>
      api.get<{ label: string; items: NetflixItem[] }>(`/tv/provider/${encodeURIComponent(key)}/trending`),
    /** Paged drill-in behind a "Hot on <service>" strip. */
    providerBrowse: (key: "apple" | "hbo" | "cable" | "nfb", page = 1) =>
      api.get<{ label: string; page: number; hasMore: boolean; items: NetflixItem[] }>(
        `/tv/provider/${encodeURIComponent(key)}/browse?page=${page}`,
      ),
    /** CuriosityStream drill-in — categories from the local catalog mirror, then items per category. */
    curiosityCategories: () =>
      api.get<{ categories: { name: string; count: number }[] }>(`/tv/curiosity/categories`),
    curiosityCategory: (name: string) =>
      api.get<{ category: string; items: CuriosityItem[] }>(`/tv/curiosity/category/${encodeURIComponent(name)}`),
    searchDebrid: (q: string, year?: number, kind?: "movie" | "series") => {
      const qs = new URLSearchParams({ q });
      if (year) qs.set("year", String(year));
      if (kind) qs.set("kind", kind);
      return api.get<{
        query: string;
        resolved: { tmdb_id: number; imdb_id: string; title: string; year: string | null; poster_url: string | null } | null;
        streams: {
          display_title: string;
          quality: string;
          hdr: boolean;
          codec: string | null;
          size_bytes: number | null;
          size_label: string | null;
          seeders: number | null;
          source: string | null;
          /** Empty on self-resolved candidates — mint it with debridResolve. */
          url: string;
          is_direct: boolean;
          info_hash?: string | null;
          file_idx?: number | null;
          filename?: string | null;
        }[];
      }>(`/tv/debrid/search?${qs.toString()}`);
    },
    listSeries: (params: { q?: string; source_id?: number; year_from?: number; year_to?: number; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && String(v).length > 0) qs.set(k, String(v));
      }
      return api.get<{
        total: number;
        limit: number;
        offset: number;
        items: { series_id: string; title: string; thumbnail_url: string | null; year: number | null; episode_count: number; sample_id: string }[];
      }>(`/tv/series?${qs.toString()}`);
    },
    seriesEpisodes: (name: string, profileId?: string) =>
      api.get<{
        series_name: string;
        episode_count: number;
        items: Array<LibraryItem & {
          tmdb_overview?: string | null;
          tmdb_backdrop_url?: string | null;
          tmdb_rating?: number | null;
          last_position_s?: number | null;
          last_event?: string | null;
          last_seen_at?: string | null;
        }>;
      }>(
        `/tv/series/episodes?name=${encodeURIComponent(name)}${profileId ? `&profile_id=${encodeURIComponent(profileId)}` : ""}`,
      ),
    seriesEpisodesRd: (imdb: string, profileId?: string) =>
      api.get<{
        series_name: string;
        imdb_id: string;
        tmdb_id: number;
        episode_count: number;
        overview: string | null;
        backdrop_url: string | null;
        poster_url: string | null;
        vote_average: number | null;
        first_air_date: string | null;
        cast?: CastMember[];
        created_by?: string | null;
        items: Array<{
          id: string;
          kind: "tv_show";
          title: string;
          season: number;
          episode: number;
          episodeTitle: string;
          thumbnail_url: string | null;
          duration_s: number | null;
          tmdb_overview: string | null;
          tmdb_rating: number | null;
          air_date: string | null;
          last_event: string | null;
          last_position_s: number | null;
        }>;
      }>(
        `/tv/series-rd/${encodeURIComponent(imdb)}/episodes${profileId ? `?profile_id=${encodeURIComponent(profileId)}` : ""}`,
      ),
    /**
     * `target` decides the auto-pick: a browser needs a codec it can decode,
     * the Pi wants the best encode available. The server cannot infer it.
     */
    item: (id: string, profileId: string, target?: "browser" | "tv", saver?: boolean) =>
      api.get<MediaItemDetail>(
        `/tv/item/${encodeURIComponent(id)}?profile_id=${encodeURIComponent(profileId)}` +
        (target === "browser" ? "&for=browser" : "") +
        (saver ? "&saver=1" : ""),
      ),
    /**
     * Every candidate release behind an rd: item, best-ranked first.
     * Named `debridSources` because `Api.sources` already means m3u/YouTube
     * ingest sources — a different concept entirely.
     */
    debridSources: (id: string, target?: "browser" | "tv") =>
      api.get<{ sources: DebridSource[]; total_candidates: number }>(
        `/tv/item/${encodeURIComponent(id)}/sources` +
        (target === "browser" ? "?for=browser" : ""),
      ),
    /**
     * Mint a playable link for an indexed candidate.
     *
     * Self-resolved candidates carry a torrent identity instead of a URL —
     * the link is created on the server, from the server's own IP, at the
     * moment of playing.
     */
    debridResolve: (hash: string, fileIdx: number | null, filename: string | null) => {
      const q = new URLSearchParams({ hash });
      if (fileIdx !== null) q.set("file_idx", String(fileIdx));
      if (filename) q.set("filename", filename);
      return api.get<{ url: string; provider: string }>(`/tv/debrid/resolve?${q.toString()}`);
    },
    /** Resolve one specific candidate release, chosen by src_id. */
    debridSource: (id: string, srcId: string, saver?: boolean) =>
      api.get<ResolvedSource>(
        `/tv/item/${encodeURIComponent(id)}/source/${encodeURIComponent(srcId)}` +
        (saver ? "?saver=1" : ""),
      ),
    event: (body: { profile_id: string; item_id: string; event: "viewed" | "completed" | "skipped" | "abandoned"; position_s?: number; duration_s?: number }) =>
      api.post<void>("/tv/events", body),
    progress: (body: { profile_id: string; item_id: string; position_s: number; duration_s?: number }) =>
      api.post<void>("/tv/progress", body),
    rate: (body: { profile_id: string; item_id: string; rating: number }) =>
      api.post<void>("/tv/ratings", body),
    comment: (body: { profile_id: string; item_id: string; text: string; llm_signals?: Record<string, number> }) =>
      api.post<{ comment: { id: number; created_at: string } }>("/tv/comments", body),
    pin: (body: { profile_id: string; item_id: string }) =>
      api.post<{ ok: boolean; position: number }>("/tv/pins", body),
    unpin: (profileId: string, itemId: string) =>
      api.del<void>(`/tv/pins/${encodeURIComponent(itemId)}?profile_id=${encodeURIComponent(profileId)}`),
    pins: (profileId: string) =>
      api.get<{
        profile_id: string;
        items: (LibraryItem & {
          position: number;
          pinned_at: string;
          is_series_pin?: boolean;
          episode_count?: number;
          backdrop_url?: string | null;
        })[];
      }>(
        `/tv/pins?profile_id=${encodeURIComponent(profileId)}`,
      ),
    reorderPins: (profileId: string, itemIds: string[]) =>
      api.post<{ ok: boolean }>("/tv/pins/reorder", { profile_id: profileId, item_ids: itemIds }),
  },
  youtubeAccount: {
    status: () => api.get<{ connected: boolean; channel_id: string | null; channel_title: string | null; scope: string | null; updated_at: string | null }>("/api/youtube/account"),
    disconnect: () => api.post<{ ok: boolean }>("/api/youtube/disconnect"),
    refresh: () => api.post<{ ok: boolean; queued?: number; error?: string }>("/api/youtube/refresh"),
  },
  debrid: {
    account: () => api.get<{
      connected: boolean;
      username?: string;
      type?: string;
      premium_days?: number;
      expiration?: string;
    }>("/admin/debrid/account"),
  },
  credentials: {
    list: () =>
      api.get<{ credentials: Array<{
        key: string;
        label: string;
        hint: string;
        set: boolean;
        masked: string | null;
        source: "db" | "env_file" | "none";
        updated_at: string | null;
        last_used_at: string | null;
      }> }>("/api/credentials"),
    save: (key: string, value: string) =>
      api.put<{ ok: boolean; key: string; masked: string }>(
        `/api/credentials/${encodeURIComponent(key)}`,
        { value },
      ),
    remove: (key: string) =>
      api.del<void>(`/api/credentials/${encodeURIComponent(key)}`),
  },

  playback: {
    devices: () =>
      api.get<{
        devices: Array<{
          id: string;
          name: string;
          wsUrl: string;
          token: string;
        }>;
      }>("/api/playback/devices"),
  },

  discover: () =>
    api.get<{
      sections: {
        kind: string;
        label: string;
        items: {
          id: string; kind: string; title: string;
          thumbnail_url: string | null; tmdb_backdrop_url: string | null;
          tmdb_rating: number | null; tmdb_genres: string[];
          tmdb_overview: string | null; streamable: boolean;
        }[];
      }[];
    }>("/tv/discover"),

  screensaver: {
    get: () => api.get<{ speed: string; doneness: string; density: number }>("/api/screensaver/settings"),
    save: (s: { speed: string; doneness: string; density: number }) =>
      api.post<{ ok: boolean }>("/api/screensaver/settings", s),
  },

  spin: {
    /** Six AI best-guess picks for right now. Each GET is a fresh spin. */
    get: (profileId: string) =>
      api.get<{ items: SpinItem[]; heat: SpinHeat }>(
        `/tv/spin?profile_id=${encodeURIComponent(profileId)}`,
      ),
    feedback: (body: { profile_id: string; item_id: string; title: string; kind: string; verdict: "up" | "down" | "skip" | "hot" }) =>
      api.post<{ ok: boolean; heat: SpinHeat }>(`/tv/spin/feedback`, body),
  },

  person: {
    /** Everything an actor/creator has done — IMDb-style, playable via rd:. */
    credits: (name: string) =>
      api.get<PersonCredits>(`/tv/person/${encodeURIComponent(name)}/credits`),
  },

  hls: {
    /**
     * Transcode progress for an HLS session.
     *
     * The playlist only advertises the segments written so far, so the player
     * cannot tell "still buffering" from "the transcoder is 40% through the
     * film". This is how the UI says which.
     */
    status: (streamUrl: string, audio: number | null, videoMode?: string | null, startS?: string | null) => {
      const q = new URLSearchParams({ url: streamUrl });
      if (audio !== null) q.set("audio", String(audio));
      // Sessions are keyed on (url, audio, videoMode, start) — an h264 or
      // resume-offset session is invisible to a lookup that omits its key.
      if (videoMode === "h264") q.set("v", videoMode);
      if (startS && Number(startS) > 0) q.set("start", startS);
      return api.get<{
        state: "idle" | "starting" | "running" | "complete" | "failed";
        key: string;
        error?: string | null;
        duration_s?: number | null;
        start_s?: number;
        media_start_s?: number;
        cached_s?: number;
        progress?: number | null;
        segments?: number;
        bytes?: number;
        forced_subtitle?: boolean;
        /** WebVTT sidecar, once the transcode has written cues into it. */
        forced_subtitle_url?: string | null;
      }>(`/api/hls/status?${q.toString()}`);
    },
  },

  cast: {
    /** TV heartbeat — call every 30 s. */
    register: (device_id: string, label: string) =>
      api.post<{ ok: boolean }>("/api/cast/register", { device_id, label }),
    /** TV poll — returns pending play command (once) or null. */
    pending: (device_id: string) =>
      api.get<{
        command: {
          item_id: string; kind: string; profile_id: string;
          title: string | null; queued_at: number;
        } | null;
      }>(`/api/cast/pending?device_id=${encodeURIComponent(device_id)}`),
    /** Phone — list known TVs (all of them; `online` is display info). */
    devices: () =>
      api.get<{
        devices: { device_id: string; label: string; last_seen_ago_s: number; online: boolean }[];
      }>("/api/cast/devices"),
    /** Phone — queue a play command. The server holds it until the TV polls
        (every ~2.5 s when online); device_id optional (defaults to the most
        recently seen TV). Never refused for "offline" — it's a queue. */
    play: (body: { item_id: string; kind: string; profile_id: string; title?: string | null; device_id?: string }) =>
      api.post<{
        ok: boolean; error?: string;
        device_id?: string; label?: string | null;
        last_seen_ago_s?: number | null; tv_online?: boolean;
      }>("/api/cast/play", body),
    /** Phone — heartbeat while browsing; signals TV to show screensaver. */
    clientHeartbeat: (client_id: string) =>
      api.post<{ ok: boolean }>("/api/cast/client/heartbeat", { client_id }),
    /** TV — check if any browser client is actively browsing. */
    clientActive: () =>
      api.get<{ active: boolean }>("/api/cast/client/active"),
    /** Phone — what's currently playing on the TV right now. */
    nowPlaying: () =>
      api.get<{ active: boolean; item_id?: string; kind?: string; title?: string | null }>("/api/cast/now-playing"),
    /** Phone — send a raw stream URL straight to a TV's mpv. Used for
        Real-Debrid / external streams that have no ingested item_id.
        sub_file_id attaches a subtitle track once the file loads.
        device_id selects which TV (multi-TV); default = the tunnel Pi. */
    mpv: (body: { url: string; start_s?: number; sub_file_id?: number | null; device_id?: string }) =>
      api.post<{ ok: boolean; error?: string }>("/api/cast/mpv", body),
    /** Phone — stop whatever mpv is currently playing on the TV. */
    stopMpv: (device_id?: string) =>
      api.post<{ ok: boolean; error?: string }>("/api/cast/mpv/stop", { device_id }),
    /** Phone-as-remote — live position/duration/pause state of the Pi's mpv. */
    mpvStatus: (deviceId: string | undefined) =>
      api.get<{ active: boolean; position?: number | null; duration?: number | null; paused?: boolean | null; volume?: number | null }>(
        `/api/cast/mpv/status${deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ""}`,
      ),
    /** Phone-as-remote — transport controls proxied to the Pi's mpv. */
    mpvCommand: (deviceId: string | undefined, action: string, value?: number) =>
      api.post<{ ok: boolean; error?: string }>("/api/cast/mpv/command", { action, value, device_id: deviceId ?? null }),
  },

  wifi: {
    /** Connection state. Open endpoint; the proxied cloud version is
        stripped to display fields unless the caller is authed (the app
        always is), in which case the Pi's full payload passes through.
        deviceId selects the Pi in a multi-Pi household (2026-10-28). */
    status: (deviceId?: string) =>
      api.get<{
        ap_mode: boolean;
        ssid: string | null;
        wifi_ip: string | null;
        ethernet_connected: boolean;
        ethernet_ip: string | null;
        installed_version?: string | null;
        signal_dbm?: number | null;
        band?: string | null;
        tx_mbps?: number | null;
        mac?: string;
        /** SSIDs only — the server never returns the stored PSKs. */
        history?: { mac: string | null; ssid: string; tried_at: string }[];
      }>(`/wifi/status${devQ(deviceId)}`),
    scan: (deviceId?: string) =>
      api.get<{ networks: { ssid: string; signal: number; security: boolean }[] }>(`/wifi/scan${devQ(deviceId)}`),
    /** Queue a join on the cloud; the named Pi picks it up over any connection. */
    queueConnect: (deviceId: string | undefined, body: { ssid: string; password: string | null }) =>
      api.post<{ ok: boolean; id?: string; error?: string }>("/api/wifi/command", { action: "connect", ...body, device_id: deviceId ?? null }),
    latestCommand: (deviceId?: string) =>
      api.get<{
        command: null | {
          id: string; action: string; ssid: string; state: string;
          result: string | null; queued_at: string; updated_at: string;
        };
      }>(`/api/wifi/command/latest${deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ""}`),
    harvest: {
      start: (deviceId: string | undefined, body: { ssid: string; ap_password: string | null }) =>
        api.post<{ ok: boolean; error?: string }>(`/wifi/harvest/start${devQ(deviceId)}`, body),
      status: (deviceId?: string) =>
        api.get<{ mode?: string; ssid?: string; error?: string; stations?: string[] }>(`/wifi/harvest/status${devQ(deviceId)}`),
      finish: (deviceId: string | undefined, body: { mac: string; password: string | null }) =>
        api.post<{ ok: boolean }>(`/wifi/harvest/finish${devQ(deviceId)}`, body),
      abort: (deviceId?: string) => api.post<{ ok: boolean }>(`/wifi/harvest/abort${devQ(deviceId)}`, {}),
    },
    bt: {
      status: (deviceId?: string) =>
        api.get<{
          phones: {
            mac: string; name: string; paired: boolean; connected: boolean;
            profile_active: boolean; bt_ip?: string | null;
          }[];
        }>(`/wifi/bt/status${devQ(deviceId)}`),
      devices: (deviceId?: string) => api.get<{ devices: { mac: string; name: string | null }[] }>(`/wifi/bt/devices${devQ(deviceId)}`),
      scanStart: (deviceId?: string) => api.post<{ ok: boolean; error?: string }>(`/wifi/bt/scan-start${devQ(deviceId)}`, {}),
      pair: (deviceId: string | undefined, body: { mac: string; name: string }) =>
        api.post<{ ok: boolean; error?: string }>(`/wifi/bt/pair${devQ(deviceId)}`, body),
      forget: (deviceId: string | undefined, body: { mac: string }) => api.post<{ ok: boolean; error?: string }>(`/wifi/bt/forget${devQ(deviceId)}`, body),
      connect: (deviceId?: string) => api.post<{ ok: boolean; phone?: string }>(`/wifi/bt/connect${devQ(deviceId)}`, {}),
      disconnect: (deviceId?: string) => api.post<{ ok: boolean }>(`/wifi/bt/disconnect${devQ(deviceId)}`, {}),
    },
  },

  /**
   * G20S remote pairing on a chosen Pi — /pair-remote/*, proxied through
   * that Pi's reverse tunnel. One remote bonds to one Pi, so every call
   * takes the target deviceId.
   */
  remote: {
    status: (deviceId?: string) =>
      api.get<{
        remotes: { mac: string; name: string; paired: boolean; trusted: boolean; connected: boolean }[];
        input_nodes: string[];
      }>(`/pair-remote/status${devQ(deviceId)}`),
    scanStart: (deviceId?: string) =>
      api.post<{ ok: boolean; scanning_seconds?: number; error?: string }>(`/pair-remote/scan-start${devQ(deviceId)}`, {}),
    devices: (deviceId?: string) =>
      api.get<{ devices: { mac: string; name: string | null }[] }>(`/pair-remote/devices${devQ(deviceId)}`),
    pair: (deviceId: string | undefined, body: { mac: string; name: string }) =>
      api.post<{ ok: boolean; remote_mac?: string; error?: string }>(`/pair-remote/pair${devQ(deviceId)}`, body),
    forget: (deviceId: string | undefined, body: { mac: string }) =>
      api.post<{ ok: boolean; error?: string }>(`/pair-remote/forget${devQ(deviceId)}`, body),
  },

  config: {
    /** Download entire config (credentials + sources + profiles) as JSON. */
    export: () => api.get<{
      version: 1;
      exported_at: string;
      credentials: { key: string; value: string }[];
      sources: {
        kind: string; label: string; url: string | null; username: string | null;
        password: string | null; refresh_minutes: number; enabled: boolean;
      }[];
      profiles: { display_name: string; avatar_emoji: string | null }[];
    }>("/admin/config/export"),
    /** Restore from a previously-exported config blob. */
    import: (config: unknown) =>
      api.post<{ ok: boolean; log: string[] }>("/admin/config/import", { config }),
  },
};
